import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

if (!customElements.get('order-by-sqft')) {
  class OrderBySqft extends HTMLElement {
    connectedCallback() {
      this.coverages = {};
      try { this.coverages = JSON.parse(this.dataset.coverages || '{}'); } catch { /* ignore */ }
      this.extraPercent = parseFloat(this.dataset.extraPercent) || 8;
      this.currentCoverage = 0;

      this.variants = [];
      this.typeOptionIndex = -1;
      this.hasTypeOption = false;
      this.activeTab = this.dataset.activeTab === 'box' ? 'box' : 'sample';

      this.qtyInput = this.querySelector('[data-qty-input]');
      this.areaInput = this.querySelector('[data-area-input]');
      this.sqftTotal = this.querySelector('[data-sqft-total]');
      this.extraCheckbox = this.querySelector('[data-extra-coverage]');

      this.querySelector('[data-action="add"]')?.addEventListener('click', () => this.adjustQty(1));
      this.querySelector('[data-action="subtract"]')?.addEventListener('click', () => this.adjustQty(-1));
      this.qtyInput?.addEventListener('change', () => this.onQtyChange());
      this.qtyInput?.addEventListener('input', () => {
        // update the area as you type, without rewriting what is in the qty box
        const qty = parseInt(this.qtyInput.value);
        if (qty >= 1) { this.syncAreaFromQty(); this.updateDisplay(); this.syncFormQty(qty); }
      });
      this.areaInput?.addEventListener('input', () => this.onAreaInput());
      this.extraCheckbox?.addEventListener('change', () => {
        if (this.areaInput?.value) this.onAreaInput();
      });

      this.initTabs();
      this.initSampleButtons();
      this.detectVariant();
      this.updateDisplay();

      this.initProductData();
      // Never leave the block hidden if the product data request stalls.
      setTimeout(() => this.markReady(), 5000);

      const section = this.closest('.shopify-section');
      if (section) {
        section.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
      }
    }

    disconnectedCallback() {
      this.stateObserver?.disconnect();
      this.stateObserver = null;
      this.buyObserver?.disconnect();
      this.buyObserver = null;
      const section = this.closest('.shopify-section');
      section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    }

    async initProductData() {
      const handle = this.dataset.productHandle;
      if (!handle) { this.markReady(); return; }

      try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) { this.markReady(); return; }
        const product = await res.json();

        this.typeOptionIndex = product.options.findIndex(opt => (typeof opt === 'string' ? opt : opt.name) === 'Type');
        this.hasTypeOption = this.typeOptionIndex >= 0;

        this.variants = product.variants.map(v => ({
          id: v.id,
          options: [v.option1, v.option2, v.option3].filter(o => o != null),
          price: v.price,
          compare_at_price: v.compare_at_price,
          available: v.available,
        }));

        if (this.hasTypeOption) {
          this.findSampleVariants();
          this.setInitialTab();
          this.deferThemeElementSetup();
        } else {
          this.markReady();
        }
      } catch (e) {
        console.warn('[order-by-sqft] Could not load product data:', e);
        this.markReady();
      }
    }

    // The tabs and buy buttons stay hidden (CSS, until data-ready is set) while the
    // variant data loads, then the selected tab is shown.
    markReady() {
      this.dataset.ready = 'true';
      if (this.stateObserver) return;
      // The theme re-renders the section on variant change and morphs attributes back to the
      // server values, so put the ready / active tab state back if it gets reset.
      this.stateObserver = new MutationObserver(() => {
        if (this.dataset.ready !== 'true') this.dataset.ready = 'true';
        if (this.dataset.activeTab !== this.activeTab) this.dataset.activeTab = this.activeTab;
      });
      this.stateObserver.observe(this, { attributes: true, attributeFilter: ['data-ready', 'data-active-tab'] });

      // The re-render also resets the buy buttons' class back to the server value.
      const section = this.closest('.shopify-section');
      if (section) {
        let queued = false;
        this.buyObserver = new MutationObserver(() => {
          if (queued) return;
          queued = true;
          requestAnimationFrame(() => {
            queued = false;
            this.syncBuyVisibility();
          });
        });
        this.buyObserver.observe(section, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
      }
    }

    setInitialTab() {
      const currentId = parseInt(this.dataset.currentVariantId);
      const currentVariant = this.variants.find(v => v.id === currentId);
      if (!currentVariant) return;

      const currentType = currentVariant.options[this.typeOptionIndex];
      const shouldBeBox = currentType === 'Box';

      const tabs = this.querySelectorAll('[data-tab]');
      const panels = this.querySelectorAll('[data-panel]');

      if (shouldBeBox) {
        tabs.forEach(t => t.classList.toggle('order-by-sqft__tab--active', t.dataset.tab === 'box'));
        panels.forEach(p => p.classList.toggle('order-by-sqft__panel--active', p.dataset.panel === 'box'));
        this.activeTab = 'box';
        this.dataset.activeTab = 'box';
      }

      const section = this.closest('.shopify-section');
      if (section) {
        section.dispatchEvent(new CustomEvent('order-by-sqft:tab-change', {
          bubbles: false,
          detail: { tab: this.activeTab },
        }));
      }
    }

    deferThemeElementSetup() {
      let attempts = 0;
      const trySetup = () => {
        const section = this.closest('.shopify-section');
        if (!section) return;
        const buyBlock = section.querySelector('.buy-buttons-block');
        if (buyBlock || attempts > 50) {
          this.hideTypeOption();
          this.applyTabVisibility();
          this.markReady();
        } else {
          attempts++;
          requestAnimationFrame(trySetup);
        }
      };
      requestAnimationFrame(trySetup);
    }

    #handleProductSelect = (event) => {
      if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

      if (event.selectedOptions) {
        this.detectVariantFromOptions(event.selectedOptions.map(o => o.value));
        this.updateDisplay();
      }

      const vid = event.detail?.variantId;
      if (this.hasTypeOption && vid) {
        const numericId = parseInt(vid);
        this.dataset.currentVariantId = vid;
        this.findSampleVariants();

        if (this.activeTab === 'sample') {
          const newVariant = this.variants.find(v => v.id === numericId);
          if (newVariant && newVariant.options[this.typeOptionIndex] === 'Box') {
            this.selectTypeInPicker('Cut Sample');
          }
        }
      }
    };

    findSampleVariants() {
      if (!this.hasTypeOption || !this.variants.length) return;

      const currentId = parseInt(this.dataset.currentVariantId);
      const currentVariant = this.variants.find(v => v.id === currentId);
      if (!currentVariant) return;

      let cutVariant = null;
      let plankVariant = null;

      for (const v of this.variants) {
        const typeVal = v.options[this.typeOptionIndex];
        if (typeVal !== 'Cut Sample' && typeVal !== 'Plank Sample') continue;

        let matches = true;
        for (let i = 0; i < v.options.length; i++) {
          if (i === this.typeOptionIndex) continue;
          if (v.options[i] !== currentVariant.options[i]) {
            matches = false;
            break;
          }
        }
        if (!matches) continue;

        if (typeVal === 'Cut Sample' && !cutVariant) cutVariant = v;
        if (typeVal === 'Plank Sample' && !plankVariant) plankVariant = v;
        if (cutVariant && plankVariant) break;
      }

      this.setSampleButton('cut', cutVariant);
      this.setSampleButton('plank', plankVariant);
    }

    setSampleButton(type, variant) {
      const btn = this.querySelector(`[data-sample-type="${type}"]`);
      if (!btn) return;

      if (variant) {
        btn.dataset.variantId = String(variant.id);
        btn.disabled = false;
        const priceEl = btn.querySelector('[data-sample-price]');
        if (priceEl) priceEl.textContent = this.formatMoney(variant.price);

        const compareEl = btn.querySelector('[data-sample-compare]');
        if (compareEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareEl.textContent = this.formatMoney(variant.compare_at_price);
            compareEl.style.display = '';
          } else {
            compareEl.style.display = 'none';
          }
        }
      } else {
        btn.disabled = true;
        btn.dataset.variantId = '';
      }
    }

    formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    }

    hideTypeOption() {
      const section = this.closest('.shopify-section');
      if (!section) return;
      const picker = section.querySelector('variant-picker');
      if (!picker) return;

      const fieldsets = picker.querySelectorAll('fieldset');
      fieldsets.forEach((fs) => {
        const legend = fs.querySelector('legend');
        if (legend && legend.textContent.trim() === 'Type') {
          fs.style.display = 'none';
        }
      });
    }

    applyTabVisibility() {
      const section = this.closest('.shopify-section');
      if (!section) return;

      this.syncBuyVisibility();

      if (this.activeTab === 'sample') {
        const currentId = parseInt(this.dataset.currentVariantId);
        const currentVariant = this.variants.find(v => v.id === currentId);
        if (currentVariant) {
          const currentType = currentVariant.options[this.typeOptionIndex];
          if (currentType === 'Box') {
            this.selectTypeInPicker('Cut Sample');
          }
        }
      } else {
        this.selectTypeInPicker('Box');
      }

      this.hideThemeQuantity();
    }

    // Sample tab: buy buttons hidden, sample credit shown. Box tab: the reverse.
    syncBuyVisibility() {
      const section = this.closest('.shopify-section');
      if (!section) return;
      const sample = this.activeTab === 'sample';
      section.querySelectorAll('.buy-buttons-block, .accelerated-checkout-block').forEach((el) => {
        el.classList.toggle('is-tab-hidden', sample);
      });
      section.querySelectorAll('.sample-credit-wrap').forEach((el) => {
        el.classList.toggle('is-tab-hidden', !sample);
      });
    }

    hideThemeQuantity() {
      const section = this.closest('.shopify-section');
      if (!section) return;
      section.querySelectorAll('.quantity-selector-wrapper').forEach(el => {
        el.style.display = 'none';
      });
    }

    selectTypeInPicker(typeValue) {
      const section = this.closest('.shopify-section');
      if (!section) return;
      const picker = section.querySelector('variant-picker');
      if (!picker) return;

      const fieldsets = picker.querySelectorAll('fieldset');
      fieldsets.forEach((fs) => {
        const legend = fs.querySelector('legend');
        if (legend && legend.textContent.trim() === 'Type') {
          const inputs = fs.querySelectorAll('input[type="radio"]');
          inputs.forEach((input) => {
            const label = input.closest('label') || input.parentElement;
            const text = label?.textContent?.trim();
            if (text === typeValue && !input.checked) {
              input.checked = true;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }
      });
    }

    initTabs() {
      const tabs = this.querySelectorAll('[data-tab]');
      const panels = this.querySelectorAll('[data-panel]');

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          tabs.forEach((t) => t.classList.remove('order-by-sqft__tab--active'));
          panels.forEach((p) => p.classList.remove('order-by-sqft__panel--active'));

          tab.classList.add('order-by-sqft__tab--active');
          const panel = this.querySelector(`[data-panel="${tab.dataset.tab}"]`);
          panel?.classList.add('order-by-sqft__panel--active');

          this.activeTab = tab.dataset.tab;
          this.dataset.activeTab = this.activeTab;

          if (this.hasTypeOption) {
            this.applyTabVisibility();
          }

          const section = this.closest('.shopify-section');
          if (section) {
            section.dispatchEvent(new CustomEvent('order-by-sqft:tab-change', {
              bubbles: false,
              detail: { tab: this.activeTab },
            }));
          }
        });
      });
    }

    initSampleButtons() {
      this.querySelectorAll('[data-add-sample]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const variantId = btn.dataset.variantId;
          if (!variantId || btn.disabled) return;
          this.addSampleToCart(parseInt(variantId), btn);
        });
      });
    }

    async addSampleToCart(variantId, btn) {
      btn.classList.add('order-by-sqft__sample-btn--adding');
      const originalText = btn.querySelector('span:last-child');
      const savedText = originalText?.textContent;
      if (originalText) originalText.textContent = 'Adding...';

      const deferredEventPromise = CartLinesUpdateEvent.createPromise();

      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(variantId), quantity: 1 }],
          promise: deferredEventPromise.promise,
        })
      );

      try {
        const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        const data = await response.json();

        if (data.status) {
          if (originalText) originalText.textContent = savedText;
          btn.classList.remove('order-by-sqft__sample-btn--adding');
          console.error('Add to cart failed:', data.message);
          return;
        }

        const cartResponse = await fetch(window.Theme?.routes?.cart_url || '/cart.js', {
          headers: { Accept: 'application/json' },
        });
        const cart = await cartResponse.json();

        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'order-by-sqft',
            itemCount: 1,
          },
        });

        if (originalText) originalText.textContent = 'Added!';
        setTimeout(() => {
          if (originalText) originalText.textContent = savedText;
          btn.classList.remove('order-by-sqft__sample-btn--adding');
        }, 1500);
      } catch (error) {
        deferredEventPromise.reject(error);
        if (originalText) originalText.textContent = savedText;
        btn.classList.remove('order-by-sqft__sample-btn--adding');
      }
    }

    detectVariant() {
      const section = this.closest('.shopify-section');
      if (!section) return;
      const pickerJson = section.querySelector('variant-picker script[type="application/json"]');
      if (pickerJson) {
        try {
          const data = JSON.parse(pickerJson.textContent);
          if (data?.options) {
            this.detectVariantFromOptions(Object.values(data.options));
          }
        } catch { /* ignore */ }
      }
    }

    detectVariantFromOptions(options) {
      if (!options) return;
      const opts = Array.isArray(options) ? options : Object.values(options);
      for (const opt of opts) {
        const handle = this.handleize(String(opt));
        if (this.coverages[handle] !== undefined) {
          this.currentCoverage = parseFloat(this.coverages[handle]) || 0;
          return;
        }
      }
    }

    handleize(str) {
      return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    }

    adjustQty(delta) {
      const current = parseInt(this.qtyInput.value) || 1;
      const next = Math.max(1, current + delta);
      this.qtyInput.value = next;
      this.syncAreaFromQty();
      this.updateDisplay();
      this.syncFormQty(next);
    }

    onQtyChange() {
      const val = Math.max(1, parseInt(this.qtyInput.value) || 1);
      this.qtyInput.value = val;
      this.syncAreaFromQty();
      this.updateDisplay();
      this.syncFormQty(val);
    }

    // Quantity -> area: sq ft covered by the chosen boxes (extra-coverage allowance removed when ticked),
    // rounded down so typing that area back gives the same box count.
    syncAreaFromQty() {
      if (!this.areaInput) return;
      if (this.currentCoverage <= 0) { this.areaInput.value = ''; return; }
      const qty = Math.max(1, parseInt(this.qtyInput.value) || 1);
      let area = qty * this.currentCoverage;
      if (this.extraCheckbox?.checked) area = area / (1 + this.extraPercent / 100);
      this.areaInput.value = Math.floor(area * 100) / 100;
    }

    onAreaInput() {
      const area = parseFloat(this.areaInput.value) || 0;
      if (area <= 0 || this.currentCoverage <= 0) return;

      let needed = area;
      if (this.extraCheckbox?.checked) {
        needed = area * (1 + this.extraPercent / 100);
      }
      const boxes = Math.ceil(needed / this.currentCoverage);
      this.qtyInput.value = Math.max(1, boxes);
      this.updateDisplay();
      this.syncFormQty(Math.max(1, boxes));
    }

    updateDisplay() {
      const qty = parseInt(this.qtyInput?.value) || 1;
      const total = this.currentCoverage > 0 ? (qty * this.currentCoverage).toFixed(2) : '—';
      if (this.sqftTotal) this.sqftTotal.textContent = total;
    }

    syncFormQty(qty) {
      const section = this.closest('.shopify-section');
      if (!section) return;
      const form = section.querySelector('form[action*="/cart/add"]');
      if (!form) return;
      const qtyField = form.querySelector('input[name="quantity"]');
      if (qtyField && qtyField !== this.qtyInput) {
        qtyField.value = qty;
        qtyField.dispatchEvent(new Event('change', { bubbles: true }));
      }
      section.dispatchEvent(new CustomEvent('order-by-sqft:qty-change', {
        bubbles: false,
        detail: { qty },
      }));
    }
  }

  customElements.define('order-by-sqft', OrderBySqft);
}


// Format Reviews.io rating display
document.addEventListener('DOMContentLoaded', function() {
  const formatRatingDisplay = () => {
    const ratingSnippets = document.querySelectorAll('.ruk_rating_snippet.normal');
    ratingSnippets.forEach(snippet => {
      // Check for existing rating span
      const existingRating = snippet.querySelector('.ruk-rating-snippet-rating');
      if (existingRating) {
        // If there are multiple rating spans, remove extras
        const allRatingSpans = snippet.querySelectorAll('.ruk-rating-snippet-rating');
        if (allRatingSpans.length > 1) {
          // Keep first one, remove the rest
          for (let i = 1; i < allRatingSpans.length; i++) {
            allRatingSpans[i].remove();
          }
        }
        return;
      }

      if (snippet.title) {
        // Extract rating and review count from title
        const matches = snippet.title.match(/(\d+\.\d+)\s+Stars\s+-\s+(\d+)\s+Reviews/);
        if (matches) {
          const [_, rating, reviewCount] = matches;

          // Find the review count span
          const countSpan = snippet.querySelector('.ruk-rating-snippet-count');
          if (countSpan) {
            // Update format to: rating (count Reviews)
            countSpan.textContent = `(${reviewCount} Reviews)`;

            // Insert single rating span before the count
            const ratingSpan = document.createElement('span');
            ratingSpan.textContent = `${rating} `;
            ratingSpan.classList.add('ruk-rating-snippet-rating');
            countSpan.parentNode.insertBefore(ratingSpan, countSpan);
          }
        }
      }
    });
  };

  const formatStickyRatingDisplay = () => {
    const ratingSnippets = document.querySelectorAll('.ruk_rating_snippet.sticky');
    ratingSnippets.forEach(snippet => {
      // Check for existing rating span
      const existingRating = snippet.querySelector('.ruk-rating-snippet-rating');
      if (existingRating) {
        // If there are multiple rating spans, remove extras
        const allRatingSpans = snippet.querySelectorAll('.ruk-rating-snippet-rating');
        if (allRatingSpans.length > 1) {
          // Keep first one, remove the rest
          for (let i = 1; i < allRatingSpans.length; i++) {
            allRatingSpans[i].remove();
          }
        }
        return;
      }

      if (snippet.title) {
        // Extract rating and review count from title
        const matches = snippet.title.match(/(\d+\.\d+)\s+Stars\s+-\s+(\d+)\s+Reviews/);
        if (matches) {
          const [_, rating, reviewCount] = matches;

          // Find the review count span
          const countSpan = snippet.querySelector('.ruk-rating-snippet-count');
          if (countSpan) {
            // Update format to: rating (count Reviews)
            countSpan.textContent = `${reviewCount} Reviews`;

            // Insert single rating span before the count
            const ratingSpan = document.createElement('span');
            ratingSpan.textContent = `${rating} out of `;
            ratingSpan.classList.add('ruk-rating-snippet-rating');
            countSpan.parentNode.insertBefore(ratingSpan, countSpan);
          }
        }
      }
    });
  };

  // Run on initial load
  formatRatingDisplay();
  formatStickyRatingDisplay();

  // Create observer to handle dynamically loaded snippets
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'data-done') {
        formatRatingDisplay();
        formatStickyRatingDisplay();
      }
    });
  });

  // Observe document body for changes
  observer.observe(document.body, {
    attributes: true,
    subtree: true,
    attributeFilter: ['data-done']
  });
});